import { useMemo } from "react";
import type { ComponentType, MouseEvent, ReactNode } from "react";
import ReactTableTrademoJs from "@shared/trademo-datatable/react-table-trademo";
import type { ResultsRowsResponse } from "@containers/Hermes/types";
import type { AlignedColumn } from "@containers/Hermes/utils/alignColumns";
import { formatCell } from "@containers/Hermes/utils/formatCell";
import { countryFlagUrl, isCountryColumn } from "@containers/Hermes/utils/countryFlag";

/**
 * One page of the full results table, drawn with the app's shared ReactTableTrademo so it looks and
 * selects like every other table. The pager stays with the panel: this holds a single page of rows (the
 * server pages them), so ReactTableTrademo's own pager is hidden and its selection is kept in step with the
 * panel's, which spans pages.
 */

// The shared table is plain JavaScript whose props are inferred as all-required; it takes the ones used here.
const ReactTableTrademo = ReactTableTrademoJs as unknown as ComponentType<Record<string, unknown>>;

export type ResultsTableRow = Record<string, string | number | null> & { id: number; __companyId: string | null };

type ResultsTableProps = {
  data: ResultsRowsResponse;
  // The serial column, and the data columns after it: `key` is the row field, `label` the header shown.
  serialColumn: string;
  columns: AlignedColumn[];
  nameColumn: string;
  // The name opens the company's profile; off for rows that are not companies.
  linkNames?: boolean;
  isSelected: (companyId: string) => boolean;
  // The rows ticked on this page, whenever that changes.
  onPageSelection: (ticked: ResultsTableRow[], page: ResultsTableRow[]) => void;
  profileHref: (companyId: string) => string | undefined;
  onOpenProfile: (event: MouseEvent<HTMLAnchorElement>, companyId: string) => void;
};

// The design's table type: 14px throughout, a grey header band, and a 36px row. ReactTableTrademo sets its own
// sizes (11px header, 12px cells, 48px rows) with !important in places, so these are scoped to its table class and
// carry !important as well.
const DESIGN_TYPE = "[&_.trademo-table-body_thead]:!tw-bg-gray-50 [&_.trademo-table-body_thead]:!tw-text-sm [&_.trademo-table-body_th]:!tw-bg-gray-50 [&_.trademo-table-body_th]:!tw-text-sm [&_.trademo-table-body_td]:!tw-text-sm [&_.trademo-table-body_td]:!tw-h-9";

export default function ResultsTable({ data, serialColumn, columns, nameColumn, linkNames = true, isSelected, onPageSelection, profileHref, onOpenProfile }: ResultsTableProps) {
  // `id` is a row's 1-based position, which is how ReactTableTrademo ties a selection to a row.
  const rows = useMemo<ResultsTableRow[]>(() => data.rows.map((row, index) => ({ ...row, id: index + 1, __companyId: data.company_ids[index] ?? null })), [data]);
  // Columns of numbers are right-aligned, as in the design.
  const numericColumns = useMemo(() => new Set(columns.filter(({ key }) => data.rows.length > 0 && data.rows.every((row) => typeof row[key] === "number")).map(({ key }) => key)), [columns, data]);
  const ticked = rows.filter((row) => row.__companyId && isSelected(row.__companyId));

  const tableColumns = useMemo(
    () => [
      { name: "#", key: "s_no", selector: "", miWidth: 60, maWidth: 70, cell: ({ row }: { row: { original: ResultsTableRow } }) => <span className="tw-text-gray-500">{row.original[serialColumn]}</span> },
      ...columns.map(({ key: column, label }) => ({
        name: numericColumns.has(column) ? <span className="tw-block tw-text-right">{label}</span> : label,
        key: column,
        selector: "",
        miWidth: column === nameColumn ? 240 : 150,
        maWidth: column === nameColumn ? 340 : 240,
        cell: ({ row }: { row: { original: ResultsTableRow } }) => {
          const value = row.original[column];
          const text = formatCell(label, value);
          const companyId = row.original.__companyId;
          let content: ReactNode = text;
          if (isCountryColumn(label) && countryFlagUrl(value)) {
            content = (
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <img src={countryFlagUrl(value)} alt="" width={20} height={14} className="tw-shrink-0 tw-rounded-[2px] tw-border tw-border-solid tw-border-gray-200 tw-object-cover" />
                {text}
              </span>
            );
          }
          if (linkNames && column === nameColumn && companyId) {
            content = (
              <a href={profileHref(companyId)} onClick={(event) => onOpenProfile(event, companyId)} target="_blank" rel="noopener noreferrer" className="tw-text-primary-purple-600 tw-font-medium hover:tw-underline tw-cursor-pointer">
                {text}
              </a>
            );
          }
          return numericColumns.has(column) ? <span className="tw-block tw-text-right">{content}</span> : content;
        },
      })),
    ],
    // The cells read the row they are given; only the links depend on the callbacks, and those are stable per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, nameColumn, serialColumn, numericColumns]
  );

  return (
    <div className={DESIGN_TYPE}>
      <ReactTableTrademo columns={tableColumns} data={rows} hideSorting hidePagination paginationTotalRows={rows.length} paginationPerPage={Math.max(rows.length, 1)} paginationDefaultPage={1} selectedData={ticked} onSelectedRowsChange={(tickedRows: ResultsTableRow[]) => onPageSelection(tickedRows, rows)} onChangePage={() => undefined} onChangeRowsPerPage={() => undefined} onTableObjectChange={() => undefined} hideSelection={!data.company_ids.some(Boolean)} />
    </div>
  );
}
